import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import DahliasShell from "../components/dahlias/DahliasShell";
import api from "../store/api";
import { useCart } from "../store/CartContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const REGIONS = [
  { v: "wellington", label: "Wellington region" },
  { v: "north_island", label: "North Island (excl. Wellington)" },
  { v: "south_island", label: "South Island" },
  { v: "rural", label: "Rural delivery" },
  { v: "pickup", label: "Local pickup (no shipping)" },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [suburb, setSuburb] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [region, setRegion] = useState("north_island");
  // Bank transfer only while we relaunch , Stripe is paused until the new
  // payment account is live.
  const [paymentMethod, setPaymentMethod] = useState("bank-transfer");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [availableCredit, setAvailableCredit] = useState(0);
  // Heritage HQ join , optional, only shown when not yet logged in
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Pre-fill from /api/customer/me when logged in
  useEffect(() => {
    api.get("/customer/me")
      .then((r) => {
        const me = r.data || {};
        setIsLoggedIn(true);
        if (me.name) setName(me.name);
        if (me.email) setEmail(me.email);
        if (me.phone) setPhone(me.phone);
        const a = me.address;
        if (a && typeof a === "object") {
          if (a.line1) setLine1(a.line1);
          if (a.line2) setLine2(a.line2);
          if (a.suburb) setSuburb(a.suburb);
          if (a.city) setCity(a.city);
          if (a.postcode) setPostcode(a.postcode);
        }
      })
      .catch(() => { setIsLoggedIn(false); });
  }, []);

  // Pull a live shipping quote whenever region or subtotal changes
  useEffect(() => {
    if (!items.length) return;
    let alive = true;
    api.post("/shipping/quote", { region, subtotal }).then((r) => {
      if (alive) setShippingCost(r.data.shipping_cost);
    }).catch(() => {});
    return () => { alive = false; };
  }, [region, subtotal, items.length]);

  // Look up the customer's Heritage Credit balance when they enter their email.
  useEffect(() => {
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setAvailableCredit(0); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.get("/checkout/credit-preview", { params: { email: trimmed } })
        .then((r) => { if (alive) setAvailableCredit(Number(r.data?.available || 0)); })
        .catch(() => { if (alive) setAvailableCredit(0); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [email]);

  const preCreditTotal = useMemo(() => subtotal + shippingCost, [subtotal, shippingCost]);
  const creditApplied = useMemo(
    () => Math.min(availableCredit, preCreditTotal),
    [availableCredit, preCreditTotal]
  );
  const total = useMemo(
    () => Math.max(0, preCreditTotal - creditApplied),
    [preCreditTotal, creditApplied]
  );

  if (!items.length) {
    return (
      <DahliasShell>
        <section className="max-w-3xl mx-auto px-5 py-20 text-center">
          <h1 className="font-display text-3xl text-[var(--dahlias-forest)]">Your cart is empty.</h1>
          <Link to="/shop" className="mt-6 inline-block text-[var(--dahlias-sage-deep)]">Visit the shop →</Link>
        </section>
      </DahliasShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !line1 || !city || !postcode) {
      toast.error("Please fill in your name, email and delivery address.");
      return;
    }
    const wantsHq = !!password;
    if (wantsHq) {
      if (password.length < 6) { toast.error("Password must be 6+ characters."); return; }
      if (password !== confirmPassword) { toast.error("Passwords don't match."); return; }
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map((l) => ({ product_id: l.product_id, variant_id: l.variant_id || null, qty: l.qty })),
        customer_name: name,
        email,
        phone: phone || undefined,
        address: { line1, line2: line2 || null, suburb: suburb || null, city, postcode, region },
        payment_method: paymentMethod,
        notes: notes || undefined,
        origin_url: window.location.origin,
        password: wantsHq ? password : undefined,
      };
      const res = await api.post("/checkout", payload);
      const { redirect_url, payment_method, hq_token, hq_login_failed, hq_needs_claim } = res.data;
      // Drop the HQ token into localStorage so the customer is auto-signed in.
      if (hq_token) {
        window.localStorage.setItem("heritage-customer-token", hq_token);
        window.dispatchEvent(new Event("heritage-customer-session"));
        toast.success("Order placed! You're now signed into Heritage HQ.");
      } else if (hq_login_failed) {
        toast.warning("Order placed, but the HQ password didn't match , please sign in separately.");
      } else if (hq_needs_claim) {
        toast.info("Order placed! You already have a Heritage HQ account , please sign in, or reset your password to set one.");
      }
      if (payment_method === "stripe") {
        // Don't clear cart yet, only clear on success poll
        window.location.href = redirect_url;
      } else {
        clear();
        navigate(redirect_url.replace(window.location.origin, ""));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Couldn't place the order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DahliasShell>
      <section data-testid="checkout-page" className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)]">Checkout</h1>

        <form onSubmit={submit} data-testid="checkout-form" className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            {/* Heritage HQ status , signed in pre-fill banner, or sign-in shortcut */}
            {isLoggedIn ? (
              <div data-testid="checkout-hq-prefilled" className="bg-[var(--dahlias-cream)] border-2 border-[var(--dahlias-sage)]/30 wobble-a p-4 text-sm text-[var(--dahlias-forest)]">
                ✓ Signed into Heritage HQ , your contact &amp; address are pre-filled.
              </div>
            ) : (
              <div className="bg-[var(--dahlias-cream)] border-2 border-dashed border-[var(--dahlias-sage)]/30 p-3 text-xs text-[var(--dahlias-forest)] flex flex-wrap items-center justify-between gap-2">
                <span>Already shopped with us?</span>
                <Link to="/account/login" state={{ from: "/checkout" }} data-testid="checkout-signin-link" className="font-label tracking-[0.25em] uppercase text-[var(--berry)] hover:underline">
                  Sign in to Heritage HQ →
                </Link>
              </div>
            )}

            {/* Contact */}
            <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-6 md:p-8">
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Contact</h2>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field id="co-name" testid="co-name" label="Full name" value={name} setValue={setName} required />
                <Field id="co-email" testid="co-email" type="email" label="Email" value={email} setValue={setEmail} required />
                <Field id="co-phone" testid="co-phone" label="Phone (optional)" value={phone} setValue={setPhone} />
              </div>

              {/* Optional HQ join , hidden when already signed in */}
              {!isLoggedIn && (
                <div className="mt-6 pt-5 border-t border-[var(--dahlias-sage)]/20" data-testid="checkout-hq-optin">
                  <div className="font-display text-base text-[var(--dahlias-forest)]">Create your Heritage HQ login (optional)</div>
                  <div className="text-xs text-[var(--dahlias-ink)]/70 mt-0.5">Track this order, download invoices &amp; access future perks. Skip to checkout as a guest.</div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field id="co-pwd" testid="co-password" type="password" label="Password (6+ chars)" value={password} setValue={setPassword} />
                    <Field id="co-pwd2" testid="co-password-confirm" type="password" label="Confirm password" value={confirmPassword} setValue={setConfirmPassword} />
                  </div>
                </div>
              )}
            </div>

            {/* Delivery */}
            <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-b p-6 md:p-8">
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Delivery</h2>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field id="co-line1" testid="co-line1" label="Address line 1" value={line1} setValue={setLine1} required />
                <Field id="co-line2" testid="co-line2" label="Address line 2" value={line2} setValue={setLine2} />
                <Field id="co-suburb" testid="co-suburb" label="Suburb" value={suburb} setValue={setSuburb} />
                <Field id="co-city" testid="co-city" label="Town / city" value={city} setValue={setCity} required />
                <Field id="co-post" testid="co-post" label="Postcode" value={postcode} setValue={setPostcode} required />
                <div>
                  <Label className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]">Region</Label>
                  <select
                    data-testid="co-region"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    className="mt-2 h-14 w-full text-base bg-[var(--dahlias-cream)] border-2 border-[var(--dahlias-sage)]/30 wobble-pill px-5"
                  >
                    {REGIONS.map((r) => (
                      <option key={r.v} value={r.v}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Payment , bank-transfer only during relaunch (Feb 24 2026) */}
            <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-c p-6 md:p-8">
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Payment</h2>
              <p className="text-sm italic text-[var(--dahlias-ink)]/70 mt-1">
                Pay securely by card, or choose bank transfer and we&apos;ll email you the account details.
              </p>
              <div className="mt-5 space-y-3" data-testid="co-payment-methods">
                {[
                  { v: "stripe", label: "Card payment (Visa, Mastercard , secure Stripe checkout)" },
                  { v: "bank-transfer", label: "Bank transfer (we'll email you the account details)" },
                ].map((m, mi) => (
                  <label key={m.v} data-testid={`co-payment-${m.v}`} className={`flex items-center gap-3 p-4 border-2 cursor-pointer ${mi % 2 === 0 ? "wobble-a" : "wobble-b"} ${
                    paymentMethod === m.v ? "border-[var(--dahlias-sage-deep)] bg-[var(--dahlias-cream)]" : "border-[var(--dahlias-sage)]/25 bg-[var(--dahlias-cream)]/50"
                  }`}>
                    <input
                      type="radio"
                      name="pm"
                      value={m.v}
                      checked={paymentMethod === m.v}
                      onChange={() => setPaymentMethod(m.v)}
                      className="w-4 h-4 accent-[var(--dahlias-sage-deep)]"
                    />
                    <span className="text-base">{m.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-5">
                <Label htmlFor="co-notes" className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]">Notes (optional)</Label>
                <Textarea
                  id="co-notes"
                  data-testid="co-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Anything we should know?"
                  className="mt-2 text-base bg-[var(--dahlias-cream)] border-[var(--dahlias-sage)]/30 wobble-a"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:col-span-5">
            <div className="sticky top-28 bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-6 md:p-8">
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Order summary</h2>
              <div className="mt-5 divide-y divide-[var(--dahlias-sage)]/15" data-testid="co-summary">
                {items.map((l) => (
                  <div key={`${l.product_id}:${l.variant_id || ""}`} className="py-3 flex items-start justify-between gap-3 text-base">
                    <div>
                      <div className="font-medium text-[var(--dahlias-forest)]">{l.name}</div>
                      {l.variant_label && <div className="text-xs text-[var(--dahlias-ink)]/65">{l.variant_label}</div>}
                      <div className="text-xs text-[var(--dahlias-ink)]/60 mt-0.5">× {l.qty}</div>
                    </div>
                    <div className="text-[var(--dahlias-ink)]">NZ${(l.unit_price * l.qty).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-[var(--dahlias-sage)]/20 space-y-2 text-base">
                <div className="flex justify-between"><span>Subtotal</span><span>NZ${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span data-testid="co-shipping">{shippingCost === 0 ? "FREE" : `NZ$${shippingCost.toFixed(2)}`}</span></div>
                {creditApplied > 0 && (
                  <div
                    data-testid="co-credit-applied"
                    className="flex justify-between text-[var(--dahlias-sage-deep)]"
                  >
                    <span className="inline-flex items-center gap-1">
                      <span aria-hidden="true">✦</span> Heritage Credit applied
                    </span>
                    <span>−NZ${creditApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-display text-2xl text-[var(--dahlias-forest)] pt-3 border-t border-[var(--dahlias-sage)]/20">
                  <span>Total</span>
                  <span data-testid="co-total">NZ${total.toFixed(2)}</span>
                </div>
                {availableCredit > creditApplied && (
                  <p className="text-xs text-[var(--dahlias-sage-deep)] mt-2" data-testid="co-credit-remaining">
                    NZ${(availableCredit - creditApplied).toFixed(2)} of Heritage Credit will remain after this order.
                  </p>
                )}
                {subtotal < 200 && (
                  <p className="text-xs text-[var(--dahlias-clay)] mt-2">Add NZ${(200 - subtotal).toFixed(2)} more for free shipping.</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="co-submit"
                className="mt-6 w-full h-14 text-base bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md"
              >
                {busy ? "Placing order…" : total <= 0 ? "Place order (paid by credit) →" : "Place order →"}
              </Button>
              <p className="text-xs text-[var(--dahlias-ink)]/55 mt-3 text-center">
                Tracked NZ Post · Free over $200 · Bank transfer , account details emailed with your order confirmation
              </p>
            </div>
          </aside>
        </form>
      </section>
    </DahliasShell>
  );
}

function Field({ id, testid, label, value, setValue, type = "text", required }) {
  return (
    <div>
      <Label htmlFor={id} className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]">{label}</Label>
      <Input
        id={id}
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required={required}
        className="mt-2 h-14 text-base bg-[var(--dahlias-cream)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
      />
    </div>
  );
}
